import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Button, Card, CircularProgress, Grid, Stack, Typography } from '@mui/material';
// components
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader } from '../common';
import Iconify from '../../../components/iconify';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getTeams } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

// Fewer cards are shown on the homepage teaser than on the team page.
const SLICE_COUNT = 4;

const ContentItemStyle = styled('div')(({ theme }) => ({
  bottom: 0,
  zIndex: 9,
  width: '100%',
  display: 'block',
  position: 'absolute',
  alignItems: 'center',
  padding: theme.spacing(3),
  borderBottomLeftRadius: 16,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)', // Fix on Mobile
  borderBottomRightRadius: 16,
  justifyContent: 'space-between',
  backgroundColor: alpha(theme.palette.grey[900], 0.72),
  flexDirection: theme.direction === 'rtl' ? 'row-reverse' : 'row',
}));

// ----------------------------------------------------------------------

TeamCard.propTypes = {
  team: PropTypes.object,
  index: PropTypes.number,
};

// - xs: 2 per row, sm: 3 per row, md and up: 4 per row.
// - Every card shares the same 3/4 media ratio so all cards stay identical in
//   size no matter how large or small the source image is.
function TeamCard({ team, index = 0 }) {
  const { name, role, avatar } = team;

  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.08 }}
        sx={{ width: '100%' }}
      >
        <Card
          sx={{
            position: 'relative',
            height: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.18)}`,
              },
              '&:hover img': { transform: 'scale(1.07)' },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 0,
              aspectRatio: '3 / 4',
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
              },
            }}
          >
            <Box component="img" alt={name} src={avatar} loading="lazy" decoding="async" />

            <ContentItemStyle>
              <Typography variant="subtitle1" sx={{ color: 'common.white' }}>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'common.white', textTransform: 'uppercase' }}>
                {role}
              </Typography>
            </ContentItemStyle>
          </Box>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

TeamWidget.propTypes = {
  isSlice: PropTypes.bool,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  intro: PropTypes.string,
  titleComponent: PropTypes.string,
  skipFetch: PropTypes.bool,
};

export default function TeamWidget({
  isSlice = false,
  eyebrow = 'Our team',
  title = 'The people behind ASAP',
  intro = 'Trainers, exam counsellors and placement specialists who mentor every student from the first mock test to the licensing exam.',
  // h1 on the team page (its primary heading), h2 on the homepage (a section).
  titleComponent = 'h2',
  // Set by the homepage, where `getHomeBundle` already filled `teams`. Left off
  // on the team page, which needs every member rather than the four it slices.
  skipFetch = false,
}) {
  const dispatch = useDispatch();
  const { teams, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    if (!skipFetch) {
      dispatch(getTeams());
    }
  }, [dispatch, skipFetch]);

  const list = Array.isArray(teams) ? teams : [];
  const visible = isSlice ? list.slice(0, SLICE_COUNT) : list;

  if (!visible.length) {
    if (!isLoading) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 8, md: 10 } }}>
        <CircularProgress size={30} thickness={4} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionHeader eyebrow={eyebrow} title={title} titleComponent={titleComponent} description={intro} />

      <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ alignItems: 'stretch' }}>
        {visible.map((team, index) => (
          <TeamCard key={team?.id || index} team={team} index={index} />
        ))}
      </Grid>

        <Stack direction="row" sx={{ mt: { xs: 3, md: 4 }, justifyContent: 'center' }}>
        <Button
          component={RouterLink}
          to="/team"
          variant="outlined"
          endIcon={<Iconify icon="eva:arrow-forward-outline" />}
        >
          Meet the full team
        </Button>
      </Stack>

    </Box>
  );
}
